import { parseExpiryInput, toLatinDigits } from '@clinic/money-date';

/**
 * Reads what a scanner or camera returns from a product label (ADR-024) and extracts what can
 * PREFILL a receipt or consumption line. Nothing here is trusted: the user confirms quantity, lot
 * and expiry (BR-26), and the server validates them again.
 *
 * Understood: GS1 element strings (GTIN `01`, expiry `17`, lot `10`, serial `21`), with the FNC1
 * group separator (ASCII 29), with the human-readable `(01)…(17)…` form, and without separators
 * where the fixed-length fields allow it; a bare 20-digit UID (Iranian authenticity label); a bare
 * GTIN-8/12/13/14. The exact content of the authenticity label's 2D code has not been confirmed
 * against real samples yet (ADR-024 prerequisite).
 */
export interface ScannedLabel {
  gtin: string | null;
  lot: string | null;
  /** `YYYY-MM-DD` (Gregorian), last day of the month when the label only gives the month. */
  expiryDate: string | null;
  serial: string | null;
  /** The 20-digit tracking id of the authenticity label (unique per unit, BR-30). */
  uid: string | null;
}

const GS = '\u001d';
/** Fixed lengths of the GS1 application identifiers we read (null = variable, up to 20). */
const FIXED: Readonly<Record<string, number | null>> = { '01': 14, '17': 6, '10': null, '21': null };

/** GS1 mod-10 check digit, for GTIN-8/12/13/14 (padded to 14). */
export function isValidGtin(value: string): boolean {
  if (!/^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(value)) return false;
  const digits = value.padStart(14, '0');
  let sum = 0;
  for (let i = 0; i < 13; i += 1) sum += Number(digits[i]) * (i % 2 === 0 ? 3 : 1);
  return (10 - (sum % 10)) % 10 === Number(digits[13]);
}

/** GS1 YYMMDD; day `00` means the last day of the month. Years 00–49 are 20xx, 50–99 are 19xx. */
function gs1Date(value: string): string | null {
  if (!/^\d{6}$/.test(value)) return null;
  const yy = Number(value.slice(0, 2));
  const year = yy < 50 ? 2000 + yy : 1900 + yy;
  const month = value.slice(2, 4);
  const day = value.slice(4, 6);
  return parseExpiryInput(day === '00' ? `${year}-${month}` : `${year}-${month}-${day}`);
}

const EMPTY: ScannedLabel = { gtin: null, lot: null, expiryDate: null, serial: null, uid: null };

function withSerial(label: ScannedLabel, serial: string): ScannedLabel {
  return { ...label, serial, uid: /^\d{20}$/.test(serial) ? serial : label.uid };
}

/** `(01)0123…(17)280531(10)A123` → fields. */
function parseParenthesized(text: string): ScannedLabel | null {
  const parts = [...text.matchAll(/\((\d{2,4})\)([^(]*)/g)];
  if (parts.length === 0 || parts.map((p) => p[0]).join('') !== text) return null;
  let label = { ...EMPTY };
  for (const [, ai, raw] of parts) {
    const value = (raw ?? '').trim();
    if (ai === '01' && isValidGtin(value)) label.gtin = value;
    else if (ai === '17') label.expiryDate = gs1Date(value);
    else if (ai === '10' && value) label.lot = value.slice(0, 20);
    else if (ai === '21' && value) label = withSerial(label, value.slice(0, 20));
  }
  return label;
}

/** Raw element string, `]d2` prefix and FNC1 separators optional. */
function parseElementString(text: string): ScannedLabel | null {
  let rest = text.replace(/^\][A-Za-z]\d/, '');
  if (!/^(01|17|10|21)/.test(rest)) return null;
  let label = { ...EMPTY };
  while (rest.length > 0) {
    if (rest.startsWith(GS)) {
      rest = rest.slice(1);
      continue;
    }
    const ai = rest.slice(0, 2);
    if (!(ai in FIXED)) return label.gtin || label.lot || label.expiryDate || label.serial ? label : null;
    rest = rest.slice(2);
    const fixed = FIXED[ai];
    let value: string;
    if (fixed) {
      value = rest.slice(0, fixed);
      rest = rest.slice(fixed);
    } else {
      // Variable length: up to the separator. A scanner that drops FNC1 leaves a lot followed by
      // a serial run together; a 20-digit serial (`21` + UID) at the very end is split off.
      const sep = rest.indexOf(GS);
      const tail = sep < 0 && ai === '10' ? /^(.+?)21(\d{20})$/.exec(rest) : null;
      const end = sep >= 0 ? sep : tail ? (tail[1] ?? '').length : rest.length;
      value = rest.slice(0, Math.min(end, 20));
      rest = rest.slice(value.length);
    }
    if (ai === '01' && isValidGtin(value)) label.gtin = value;
    else if (ai === '17') label.expiryDate = gs1Date(value);
    else if (ai === '10' && value) label.lot = value;
    else if (ai === '21' && value) label = withSerial(label, value);
  }
  return label;
}

/** What a scan says; null when nothing usable was found. */
export function parseProductBarcode(input: string): ScannedLabel | null {
  const text = toLatinDigits(input).replace(/[\r\n]+$/, '').trim();
  if (!text) return null;
  if (/^\d{20}$/.test(text)) return { ...EMPTY, uid: text, serial: text };
  if (/^\d{8,14}$/.test(text)) return isValidGtin(text) ? { ...EMPTY, gtin: text.padStart(14, '0') } : null;
  const label = text.startsWith('(') ? parseParenthesized(text) : parseElementString(text);
  if (!label) return null;
  if (label.gtin) label.gtin = label.gtin.padStart(14, '0');
  return label.gtin || label.lot || label.expiryDate || label.uid ? label : null;
}

